import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, TextField, Button, Autocomplete } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Api from "../../../utils/Api";
import Layout from "../../../Layout";
import Swal from "sweetalert2";
import HeaderBread from "../../../components/HeaderBread";
import { addlog } from "../../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../../utils/Constant/LogConstant";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import { fetchAllPorts } from "../../../utils/ApiCalls/ShipItineraries";

const navigation = [
    { title: "Home", route: "/" },
    { title: "Itinerary List", route: "/website/cms/packages" },
];

const EditItinerary = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [packageInfo, setPackageInfo] = useState(null);
    const [itineraryData, setItineraryData] = useState([]);
    const [updating, setUpdating] = useState(false);
    const [originalData, setOriginalData] = useState([]);
    const [portNameErrors, setPortNameErrors] = useState([]);
    const [portOptions, setPortOptions] = useState([]);
    const [{ userDetails }] = useDataLayerValue();

    const navigate = useNavigate();
    const location = useLocation();


    const fetchPackageItinerary = async () => {
        try {
            setLoading(true);
            const res = await Api.get(`/ship-itineraries/package-itineraries/${id}`);
            const { packageInformation, arrangedItinerary } = res?.data?.data || {};
            setPackageInfo(packageInformation);
            setItineraryData(arrangedItinerary || []);
            setOriginalData(JSON.parse(JSON.stringify(arrangedItinerary || [])));
        } catch (e) {
            console.error("Error fetching package itinerary:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPackageItinerary();
        fetchAllPorts().then((data) => {
            setPortOptions(
                data.filter(
                    (p) =>
                        p.city &&
                        p.api_city_code &&
                        p.api_city_code.trim() !== ""
                )
            );
        });
    }, [id]);


    const handleInputChange = (index, field, value) => {
        const updated = [...itineraryData];
        updated[index][field] = value;
        setItineraryData(updated);
    };

    const handleAddDay = () => {
        if (!packageInfo) return;

        const newRow = {
            id: Date.now(),
            agencyId: 1,
            itineraryId: packageInfo?.itinerary_id || "",
            packageId: packageInfo?.package_id || "",
            isExtra: 1,
            isNew: true,
            portCode: "",
            displayPortName: "",
            portName: "",
            departureDay: 0,
            departureTime: "00:00",
            displayDepartureTime: "00:00",
            arrivalTime: "00:00",
            displayArrivalTime: "00:00",
            portCountryCode: "",
        };

        // day / sequenceNumber / arrivalDay are assigned by renumberNewRows
        setItineraryData((prev) => renumberNewRows([...prev, newRow]));
    };

    // Keep saved rows as returned by the API; number unsaved rows after the last saved day.
    // Server day = arrivalDay + 1 (arrivalDay is a 0-based offset).
    const renumberNewRows = (rows) => {
        const savedRows = rows.filter((item) => !item.isNew);
        const lastSavedDay = savedRows.reduce((max, item) => Math.max(max, item.day || 0), 0);
        const lastSavedSeq = savedRows.reduce((max, item) => Math.max(max, item.sequenceNumber || 0), 0);
        let offset = 0;
        return rows.map((item) => {
            if (!item.isNew) return item;
            offset++;
            const day = lastSavedDay + offset;
            return {
                ...item,
                day,
                sequenceNumber: lastSavedSeq + offset,
                arrivalDay: day - 1,
            };
        });
    };

    const handleDeleteRow = async (index) => {
        const row = itineraryData[index];
        if (!row) return;

        if (row.isNew || !row.id) {
            setItineraryData((prev) => renumberNewRows(prev.filter((_, i) => i !== index)));
            return;
        }

        if (row.isExtra === 1) {
            const confirmDelete = await Swal.fire({
                title: "Delete this itinerary?",
                text: "This will permanently delete this itinerary entry.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete it",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });

            if (!confirmDelete.isConfirmed) return;

            try {
                await Api.delete(`/ship-itineraries/ship-itineray/${row.id}/${row.packageId}`);
                setItineraryData((prev) => renumberNewRows(prev.filter((_, i) => i !== index)));

                Swal.fire({
                    icon: "success",
                    title: "Deleted!",
                    text: "Itinerary row deleted successfully.",
                    timer: 1200,
                    showConfirmButton: false,
                });
                  addlog(LOG_ACTIONS.REMOVED, LOG_TYPES.Packages, {
                    packageId: row.packageId,
                    itineraryId: row.id,
                    title: `Port ${row.portName} of ${packageInfo?.package_id}`,
                    id: row.id,
                } ,userDetails?.id );

                await fetchPackageItinerary();
            } catch (error) {
                console.error("Delete API failed:", error);
                Swal.fire({
                    icon: "error",
                    title: "Delete Failed",
                    text: error.message || "Unable to delete this itinerary row.",
                });
            }
        }
    };

    const handleUpdate = async () => {
        for (let row of itineraryData) {
            if (
                !row.portName ||
                !row.displayPortName
                // !row.arrivalTime ||
                // !row.displayArrivalTime ||
                // !row.departureTime ||
                // !row.displayDepartureTime
            ) {
                Swal.fire({
                    icon: "warning",
                    title: "Missing Fields",
                    text: "Please fill all mandatory fields before updating.",
                });
                return;
            }
        }

        for (let i = 0; i < itineraryData.length; i++) {
            if (portNameErrors[i]) {
                Swal.fire({
                    icon: "warning",
                    title: "Invalid Port Name",
                    text: "Please correct the invalid port names before updating.",
                });
                return;
            }
        }

        addlog(
            LOG_ACTIONS.UPDATED,
            LOG_TYPES.Packages,
            {
            packageId: packageInfo?.package_id,
            itineraryId: packageInfo?.itinerary_id,
            id: id,
            title:`Itinerary for Itinerary Id ${packageInfo?.package_id}`
        } ,userDetails?.id );


        const addedRows = itineraryData.filter((item) => item.isExtra === 1 && item.isNew);

        const lastExistingSeq =
            originalData.length > 0
                ? Math.max(...originalData.map((d) => d.sequenceNumber || 0)) //
                : 0;

        const lastExistingDay =
            originalData.length > 0
                ? Math.max(...originalData.map((d) => d.day || 0)) //
                : 0;

        const newItineraryData = addedRows.map((r, i) => {
            const sequenceNumber = lastExistingSeq + (i + 1);
            // arrivalDay is a 0-based offset on the server (day = arrivalDay + 1)
            const arrivalDay = lastExistingDay + i;

            const departureDay =
                addedRows.length === 1
                    ? 0
                    : i === addedRows.length - 1
                        ? 0
                        : arrivalDay;

            return {
                portName: r.portName,
                displayPortName: r.displayPortName,
                depatureDay: departureDay,
                sequenceNumber,
                arrivalDay,
                departureTime: r.departureTime,
                displayDepartureTime: r.displayDepartureTime,
                arrivalTime: r.arrivalTime,
                displayArrivalTime: r.displayArrivalTime,
                portCode: r.portCode,
            };
        });


        const exitingItineraryData = itineraryData
            .filter((item) => (item.isExtra === 0 || (item.isExtra === 1 && !item.isNew)) && item.id)
            .filter((item) => {
                const original = originalData.find((o) => String(o.id) === String(item.id));
                if (!original) return false;

                const normalize = (val) => (val == null ? "" : String(val).trim());

                return (
                    normalize(item.portName) !== normalize(original.portName) ||
                    normalize(item.displayPortName) !== normalize(original.displayPortName) ||
                    normalize(item.arrivalTime) !== normalize(original.arrivalTime) ||
                    normalize(item.displayArrivalTime) !== normalize(original.displayArrivalTime) ||
                    normalize(item.departureTime) !== normalize(original.departureTime) ||
                    normalize(item.displayDepartureTime) !== normalize(original.displayDepartureTime) ||
                    normalize(item.departureDay) !== normalize(original.departureDay) ||
                    normalize(item.arrivalDay) !== normalize(original.arrivalDay)
                );
            })
            .map((r) => ({
                id: r.id,
                portName: r.portName,
                displayPortName: r.displayPortName,
                depatureDay: r.departureDay,
                sequenceNumber: r.sequenceNumber,
                arrivalDay: r.arrivalDay,
                departureTime: r.departureTime,
                displayDepartureTime: r.displayDepartureTime,
                arrivalTime: r.arrivalTime,
                displayArrivalTime: r.displayArrivalTime,
                portCode: r.portCode,
                portCountryCode: r.portCountryCode,
                isExtra: r.isExtra,
            }));



        const payload = {
            packageId: packageInfo?.package_id,
            itineraryId: packageInfo?.itinerary_id,
            newItineraryData,
            exitingItineraryData,
        };

        try {
            setUpdating(true);
            const res = await Api.post(`/ship-itineraries/ship-itineray`, payload);
            if (res?.data?.status) {
                Swal.fire({
                    icon: "success",
                    title: "Updated!",
                    text: "Itinerary updated successfully.",
                    timer: 1800,
                    showConfirmButton: false,
                });
                setOriginalData(itineraryData);
                await fetchPackageItinerary();
                navigate(`/shore/mobile-masters/packages${location.search}`, { replace: true });

            } else {
                throw new Error(res?.data?.message || "Update failed.");
            }
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Update Failed",
                text: err.message || "Could not update itinerary.",
            });
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Layout type={"update_per"} module_data={"Packages"}>
            <HeaderBread navigation={navigation} title={"Itinerary"} />
            <Box sx={{ padding: 3 }}>
                {/* PACKAGE INFO */}
                <Paper sx={{ padding: 2, marginBottom: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Itinerary Information
                    </Typography>
                    {packageInfo ? (
                        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
                            <Box>
                                <Typography fontWeight="bold" color="text.secondary">
                                    Itinerary Id
                                </Typography>
                                <Box sx={{ background: "#f0f0f0", p: 1, borderRadius: 1, fontWeight: "bold" }}>
                                    {packageInfo.itinerary_id}
                                </Box>
                            </Box>
                            <Box>
                                <Typography fontWeight="bold" color="text.secondary">
                                    Travel Itinerary Title
                                </Typography>
                                <Typography fontWeight="bold">{packageInfo.package_name}</Typography>
                            </Box>
                            <Box>
                                <Typography fontWeight="bold" color="text.secondary">
                                    Cruise Id (Sailing Dates)
                                </Typography>
                                <Box sx={{ background: "#f0f0f0", p: 1, borderRadius: 1, fontWeight: "bold" }}>
                                    {packageInfo.package_id}
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Typography>No Itinerary Information Found</Typography>
                    )}
                </Paper>

                {/* ITINERARY TABLE */}
                <Paper sx={{ padding: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight="bold">
                            Itinerary {packageInfo?.itinerary_id}
                        </Typography>
                        <Button variant="outlined" onClick={handleAddDay}>
                            Add Day
                        </Button>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#e0b87f" }}>
                                    <TableCell>Day</TableCell>
                                    <TableCell>Port</TableCell>
                                    <TableCell>Display Port</TableCell>
                                    <TableCell>Arrival</TableCell>
                                    <TableCell>Display Arrival</TableCell>
                                    <TableCell>Departure</TableCell>
                                    <TableCell>Display Departure</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {itineraryData.map((row, index) => (
                                    <TableRow key={row.id || index}>
                                        <TableCell>
                                            <TextField value={row.day || ""} size="small" disabled />
                                        </TableCell>
                                        <TableCell>
                                            <Autocomplete
                                                options={portOptions}
                                                getOptionLabel={(option) =>
                                                    typeof option === "string"
                                                        ? option
                                                        : `${option.city} (${option.api_city_code})`
                                                }
                                                value={portOptions.find((p) => p.city === row.portName) || null}
                                                disabled={!row.id}
                                                size="small"
                                                onChange={(_, newValue) => {
                                                    if (newValue) {
                                                        handleInputChange(index, "portName", newValue.city);
                                                        handleInputChange(index, "portCode", newValue.api_city_code || "");
                                                        setPortNameErrors((prev) => {
                                                            const next = [...prev];
                                                            next[index] = null;
                                                            return next;
                                                        });
                                                    } else {
                                                        handleInputChange(index, "portName", "");
                                                        handleInputChange(index, "portCode", "");
                                                        setPortNameErrors((prev) => {
                                                            const next = [...prev];
                                                            next[index] = "Port can't be empty";
                                                            return next;
                                                        });
                                                    }
                                                }}
                                                sx={{ minWidth: 180 }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        required
                                                        error={!!portNameErrors[index]}
                                                        helperText={portNameErrors[index]}
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Autocomplete
                                                options={portOptions}
                                                 getOptionLabel={(option) =>
                                                    typeof option === "string"
                                                        ? option
                                                        : `${option.city} (${option.api_city_code})`
                                                }
                                                value={portOptions.find((p) => p.city === row.displayPortName) || null}
                                                disabled={!row.id}
                                                size="small"
                                                onChange={(_, newValue) => {
                                                    handleInputChange(index, "displayPortName", newValue ? newValue.city : "");
                                                }}
                                                sx={{ minWidth: 180 }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        required
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <TextField
                                                value={row.arrivalTime || ""}
                                                size="small"
                                                required
                                                onChange={(e) => handleInputChange(index, "arrivalTime", e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.displayArrivalTime}
                                                size="small"
                                                required
                                                onChange={(e) => handleInputChange(index, "displayArrivalTime", e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.departureTime}
                                                size="small"
                                                required
                                                onChange={(e) => handleInputChange(index, "departureTime", e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                            // type="time"
                                                value={row.displayDepartureTime}
                                                size="small"
                                                required
                                                onChange={(e) => handleInputChange(index, "displayDepartureTime", e.target.value)}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            {row.isExtra === 1 && (
                                                <IconButton color="error" onClick={() => handleDeleteRow(index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box display="flex" justifyContent="flex-end" mt={3}>
                        <Button variant="contained" color="primary" onClick={handleUpdate} disabled={updating}>
                            {updating ? "Updating..." : "Update"}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Layout>
    );
};

export default EditItinerary;